import type { Call } from '@bcio-voice/contracts';
import type { WorkspaceValue } from '../../state/workspace.tsx';
import { checkDestination, type DestinationDraft } from '../../call/destination.ts';
import { isSessionOutboundAdmin, resolveOutboundLineSelection } from '../../call/pstn.ts';
import { capability, hasPermission, usable } from '../../lib/capabilities.ts';
import { blocklistAccess, callNumber, readLookup } from '../blocklist/model.ts';
import { BlocklistClient } from '../../lib/api.ts';

export type CallbackPlan = { visible: boolean; draft: DestinationDraft | null; reason: string | null };

/** Prepare a callback through the normal dialer. POST /calls remains the authoritative DNC/policy gate. */
export function callbackPlan(call: Call, ws: WorkspaceValue): CallbackPlan {
  if (ws.session.view_as || !hasPermission(ws.permissions, 'calls:create')) return { visible: false, draft: null, reason: null };
  const refuse = (reason: string): CallbackPlan => ({ visible: true, draft: null, reason });
  if (call.state !== 'ended' && call.state !== 'failed') return refuse('This call is still in progress.');
  if (ws.liveCalls.length || ws.callControl.starting.length || ws.bridge.calls.some(c => c.phase !== 'ended' && c.phase !== 'failed')) {
    return refuse('Finish your current call before calling back.');
  }
  if (call.direction === 'internal') {
    const other = call.destination.kind === 'internal' && call.destination.user_id !== ws.userId
      ? call.destination.user_id : call.participants.find(p => p.kind === 'agent' && p.user_id && p.user_id !== ws.userId)?.user_id;
    if (!other) return refuse('No teammate was recorded for this call.');
    if (!usable(capability(ws.config.value, 'calling.internal'))) return refuse('Internal calling is not available for your account.');
    const draft: DestinationDraft = { kind: 'internal', userId: other };
    const check = checkDestination(draft, ws.userId);
    return check.ok ? { visible: true, draft, reason: null } : refuse(check.message);
  }
  const number = callNumber(call);
  if (!number) return refuse('The caller did not provide a callable number.');
  if (!usable(capability(ws.config.value, 'calling.pstn.outbound'))) return refuse('Outside calling is not available for your account.');
  // Inbound `from` is the external caller, never our outbound line. Only reuse an outbound line if this user may use it.
  const lines = resolveOutboundLineSelection(ws.numbers.value ?? [], call.direction === 'outbound' ? call.from.number_id : null, { isAdmin: isSessionOutboundAdmin(ws.session) });
  const draft: DestinationDraft = { kind: 'pstn', input: number, fromNumberId: lines.effectiveFromId };
  const check = checkDestination(draft, ws.userId);
  return check.ok ? { visible: true, draft, reason: null } : refuse(lines.exhaustedBlockReason ?? check.message);
}

/** Resolve again at activation time; never queue a callback behind a ringing/active call. */
export function openCallback(call: Call, ws: WorkspaceValue, navigate: (path: string) => void): string | null {
  const plan = callbackPlan(call, ws);
  if (!plan.visible || !plan.draft) return plan.reason ?? 'Calling is not available for your account.';
  ws.requestDial(plan.draft, call.direction === 'internal' ? null : call.contact_id ?? null);
  navigate('/phone');
  return null;
}

/** Optional list-reader preflight; callers without list permission still use POST /calls' mandatory DNC check. */
export async function callbackDncReason(draft: DestinationDraft, ws: WorkspaceValue): Promise<string | null> {
  if (draft.kind !== 'pstn' || !blocklistAccess(ws.permissions).read) return null;
  try {
    const lookup = readLookup(await new BlocklistClient(ws.api).lookup(draft.input));
    if (!lookup) return 'Could not check the do-not-call list. Try again.';
    return lookup.blocked && lookup.entry?.lists.includes('calls_outbound') ? 'This number is on the do-not-call list.' : null;
  } catch {
    return 'Could not check the do-not-call list. Try again.';
  }
}

/** A user-activated click-to-call from a contact, voicemail or historical external number. */
export function externalNumberPlan(number:string,ws:WorkspaceValue,preferredLine:string|null=null):CallbackPlan{
 if(ws.session.view_as||!hasPermission(ws.permissions,'calls:create'))return {visible:false,draft:null,reason:null};
 const refuse=(reason:string):CallbackPlan=>({visible:true,draft:null,reason});
 if(ws.liveCalls.length||ws.callControl.starting.length||ws.bridge.calls.some(c=>c.phase!=='ended'&&c.phase!=='failed'))return refuse('Finish your current call before starting another.');
 if(!usable(capability(ws.config.value,'calling.pstn.outbound')))return refuse('Outside calling is not available for your account.');
 if(ws.readiness.state!=='ready')return refuse(ws.readiness.reason??'Your phone is not ready. Open Phone to connect it.');
 const lines=resolveOutboundLineSelection(ws.numbers.value??[],preferredLine,{isAdmin:isSessionOutboundAdmin(ws.session)});
 const draft:DestinationDraft={kind:'pstn',input:number,fromNumberId:lines.effectiveFromId};
 const check=checkDestination(draft,ws.userId);
 return check.ok?{visible:true,draft,reason:null}:refuse(lines.exhaustedBlockReason??check.message);
}
